import TitlePage from '../../components/TitlePage';
import H1Styled from '../../components/Typography/H1Styled';
import AnzianitaTable from './Tables/AnzianitaTable';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { downloadProfilesCSV, getProfiles } from '../../api/profile';
import { useSearchParams } from 'react-router-dom';
import Pagination from '../../components/Pagination';
import { useAuth } from '../../contexts/Auth';
import { useDebounce } from 'use-debounce';
import { FilterType } from '../../interfaces/filters';
import FiltersBar from '../../components/Filters/FiltersBar';
import sectionsAPIs from '../../api/section';
import { isDelegato } from '../../libs/utils/auth';
import { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { DownloadIcon } from '@heroicons/react/solid';
import { toast } from 'react-toastify';
import PrimaryButton from '../../components/Buttons/PrimaryButton';
import { getGroupFilter } from '../../libs/utils/helpers';
import { getQuerySearchFilters } from '../../libs/utils/profile';
import ConsegnaSpillaModal, {
  BulkUpdateProfilesResponse,
} from '../../components/Modals/ConsegnaSpillaModal';
import CreateNewParticipantsAudienceModal from '../../components/Modals/CreateNewParticipantsAudienceModal';
import { ProfileAttributes, ProfileListResponse } from '../../interfaces/profile';
import { createAudience } from '../../api/audiences';
import {
  ParticipantsAudienceForm,
  ParticipantsAudienceType,
} from '../../interfaces/audiences';
import _ from 'lodash';

const LIST_QUERY_KEY = 'usersListAnzianita';

type PinDeliveredPatch = Pick<
  ProfileAttributes,
  'pin10YearsDelivered' | 'pin25YearsDelivered' | 'pin50YearsDelivered'
>;

function extractPinPatchFromBulkEntity(
  item: Record<string, unknown>
): Partial<PinDeliveredPatch> {
  const src =
    (item.attributes as Record<string, unknown> | undefined) ?? item;
  const out: Partial<PinDeliveredPatch> = {};
  if (typeof src.pin10YearsDelivered === 'boolean') {
    out.pin10YearsDelivered = src.pin10YearsDelivered;
  }
  if (typeof src.pin25YearsDelivered === 'boolean') {
    out.pin25YearsDelivered = src.pin25YearsDelivered;
  }
  if (typeof src.pin50YearsDelivered === 'boolean') {
    out.pin50YearsDelivered = src.pin50YearsDelivered;
  }
  return out;
}

/** Applica alla lista i boolean spilla restituiti dal bulk-update (subito coerente con il DB). */
function mergeBulkPinUpdatesIntoListData(
  list: ProfileListResponse | undefined,
  updatedEntities: Record<string, unknown>[]
): ProfileListResponse | undefined {
  if (!list?.data?.length || !updatedEntities?.length) return list;
  const pinByProfileId = new Map<number, Partial<PinDeliveredPatch>>();
  for (const raw of updatedEntities) {
    const id = Number(raw.id);
    if (!Number.isFinite(id)) continue;
    const patch = extractPinPatchFromBulkEntity(raw);
    if (Object.keys(patch).length > 0) pinByProfileId.set(id, patch);
  }
  if (pinByProfileId.size === 0) return list;
  return {
    ...list,
    data: list.data.map((row) => {
      const pinPatch = pinByProfileId.get(row.id);
      if (!pinPatch) return row;
      return {
        ...row,
        attributes: { ...row.attributes, ...pinPatch },
      };
    }),
  };
}

/** Populate minimo per tabella Anzianità + delegazione (backend usa section.region). */
const ANZIANITA_LIST_POPULATE = ['badge', 'section.region', 'user'] as const;

/**
 * Costruisce il body query Strapi per la lista.
 * `debounced`: ricerca e filtri lenti (useDebounce).
 * `live`: paginazione e ordinamento sempre allineati alla URL corrente.
 */
function buildAnzianitaListQuery(
  debounced: URLSearchParams,
  live: URLSearchParams
) {
  const searchInput = debounced.get('search');
  let searchFilters: object[] = [];

  if (searchInput) {
    if (searchInput.trim().split(/\s+/).length === 1) {
      if (!Number.isNaN(parseInt(searchInput, 10))) {
        searchFilters = searchFilters.concat({
          badge: { badgeNumber: { $contains: searchInput } },
        });
      } else {
        searchFilters = getQuerySearchFilters(searchInput);
        searchFilters = searchFilters.concat({
          user: { email: { $contains: searchInput } },
        });
      }
    } else {
      searchFilters = getQuerySearchFilters(searchInput);
    }
  }

  let sortParams: Record<string, string> = {};
  try {
    sortParams = JSON.parse(live.get('sort') ?? '{}');
  } catch {
    sortParams = { updatedAt: 'desc' };
  }

  const anzianitaBracket = getGroupFilter({
    filterName: 'anzianita',
    searchParams: debounced,
  })?.[0];

  return {
    populate: [...ANZIANITA_LIST_POPULATE],
    filters: {
      ...(searchFilters.length > 0 ? { $or: searchFilters } : {}),
      section: getGroupFilter({
        filterName: 'sections',
        searchParams: debounced,
      }),
      secondarySections: getGroupFilter({
        filterName: 'secondarySections',
        searchParams: debounced,
      }),
      ...(anzianitaBracket
        ? { anzianitaBracket: { $eq: String(anzianitaBracket) } }
        : {}),
    },
    pagination: {
      page: live.get('page') || '1',
      pageSize: live.get('pageSize') || '20',
    },
    sort:
      Object.keys(sortParams).length === 0
        ? { updatedAt: 'desc' }
        : sortParams,
  };
}

const ListaAnzianita = () => {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const [{ token, profile }] = useAuth();
  const { REACT_APP_DEBOUNCE_DELAY } = process.env;
  const delay = Number(REACT_APP_DEBOUNCE_DELAY);
  const [debounceSearchParams] = useDebounce(searchParams, delay);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  const profilesListQuery = useMemo(
    () => buildAnzianitaListQuery(debounceSearchParams, searchParams),
    [debounceSearchParams, searchParams]
  );

  const usersListQuery = useQuery(
    [LIST_QUERY_KEY, profilesListQuery],
    () =>
      getProfiles({
        token,
        query: profilesListQuery,
      }),
    {
      enabled: Boolean(token),
    }
  );

  const applyPinDeliveryToListCaches = (bulk: BulkUpdateProfilesResponse) => {
    const rows = bulk?.data;
    if (!rows?.length) return;
    queryClient.setQueriesData([LIST_QUERY_KEY], (old) =>
      mergeBulkPinUpdatesIntoListData(old as ProfileListResponse | undefined, rows)
    );
  };

  useEffect(() => {
    setSelectedIds(new Set());
  }, [debounceSearchParams]);

  const filterBar = useMemo(() => {
    const sectionFilters = {
      type: FilterType.MULTISELECT,
      attribute: 'sections',
      label: 'Sezioni',
      searchForAttributes: ['name'],
      key: 'sectionsListFilters',
      source: (data: any) =>
        sectionsAPIs.findOptions({
          ...data,
          query: { ...data.query },
        }),
    };

    const secondarySectionsFilters = {
      type: FilterType.MULTISELECT,
      attribute: 'secondarySections',
      label: 'Sezioni seguite',
      searchForAttributes: ['name'],
      key: 'secondarySectionsListFilters',
      source: (data: any) =>
        sectionsAPIs.findOptions({
          ...data,
          query: { ...data.query },
        }),
    };

    const ANZIANITA_OPTIONS = [
      {
        attributes: { name: '10+ anni (10-24 consecutivi)' },
        id: '10',
      },
      {
        attributes: { name: '25+ anni (25-49 consecutivi)' },
        id: '25',
      },
      { attributes: { name: '50+ anni' }, id: '50' },
    ];

    const filters = [
      {
        type: FilterType.MULTISELECT,
        attribute: 'anzianita',
        label: 'Anzianità',
        key: 'anzianitaFilter',
        source: () => ({ data: ANZIANITA_OPTIONS }),
        isSearchable: false,
        isMutualEsclusion: true,
      },
      {
        type: FilterType.SEARCH_BAR,
        attribute: 'search',
        label: 'Cerca...',
      },
    ];

    if (isDelegato(profile)) {
      return [sectionFilters, ...filters];
    }

    return [sectionFilters, secondarySectionsFilters, ...filters];
  }, [profile]);

  const { mutate: createAudienceMutation, isLoading: isCreatingAudience } =
    useMutation({
      mutationFn: createAudience,
      onSuccess: () => {
        toast.success('Audience salvata con successo');
        queryClient.refetchQueries(['audiences'], { exact: false });
      },
      onError: (err: any) => {
        toast.error(
          err?.response?.data?.error?.message || "Qualcosa e' andato storto!"
        );
      },
    });

  const onCreateAnzianitaAudience = (values: ParticipantsAudienceForm) => {
    const baseFilters = _.omit(profilesListQuery, 'pagination');
    const audienceQuery =
      values.type === ParticipantsAudienceType.WHOLE
        ? baseFilters
        : {
            ...baseFilters,
            filters: { id: { $in: [...selectedIds] } },
          };
    createAudienceMutation({
      data: { data: { title: values.title, audienceQuery } },
      token,
    });
  };

  const { mutate: usersListCSVMutation, isLoading: isDownloadingCSV } =
    useMutation('downloadUsersAnzianitaCSV', downloadProfilesCSV, {
      onError: () => {
        toast.error('Ooops... Qualcosa è andato storto.');
      },
      onSuccess: () => {
        if (Number(usersListQuery.data?.meta?.pagination?.total) >= 30000)
          toast.warning(
            "Documento CSV scaricato con successo.\nL'export csv è stato limitato a 30000 elementi."
          );
        else toast.success('Documento CSV scaricato con successo');
      },
    });

  const toggleRow = (id: number, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const selectAllCurrentPage = (select: boolean) => {
    const rows = usersListQuery.data?.data ?? [];
    const ids = rows.map((r) => r.id);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (select) ids.forEach((id) => next.add(id));
      else ids.forEach((id) => next.delete(id));
      return next;
    });
  };

  return (
    <>
      <TitlePage title='Utenti — Anzianità' />

      <div className='col-span-12 lg:col-span-9 xl:col-span-10'>
        <H1Styled>Anzianità</H1Styled>

        <div className='space-y-5'>
          <FiltersBar filters={filterBar} />
          <div className='flex justify-end w-full gap-4'>
            <CreateNewParticipantsAudienceModal
              buttonType='text'
              buttonTxt='Salva Audience'
              selectedParticipantsNumber={selectedIds.size}
              totalParticipantsNumber={
                usersListQuery.data?.meta?.pagination?.total ?? 0
              }
              onSubmit={onCreateAnzianitaAudience}
              isLoading={isCreatingAudience}
            />
            <ConsegnaSpillaModal
              selectedIds={[...selectedIds]}
              onSuccess={async (bulkResult) => {
                applyPinDeliveryToListCaches(bulkResult);
                setSelectedIds(new Set());
                await queryClient.refetchQueries([LIST_QUERY_KEY], {
                  active: true,
                });
              }}
            />
            <PrimaryButton
              textSmall
              disabled={isDownloadingCSV}
              isLoading={isDownloadingCSV}
              onClick={() => {
                usersListCSVMutation({
                  token,
                  body: {},
                  query: profilesListQuery,
                  fileName: `Lista anzianità ONAV ${moment().format('DD-MM-YY')}`,
                });
              }}
            >
              <span className='text-sm underline flex flex-row'>
                <DownloadIcon className='w-4 h-4 mt-[3px]' /> <p>Scarica CSV</p>
              </span>
            </PrimaryButton>
          </div>
          <AnzianitaTable
            query={usersListQuery}
            selectedIds={selectedIds}
            onToggle={toggleRow}
            onSelectAllCurrentPage={selectAllCurrentPage}
          />
          <Pagination
            pagination={usersListQuery.data?.meta.pagination}
            defaultPageSize={20}
          />
        </div>
      </div>
    </>
  );
};

export default ListaAnzianita;
